/** The sample café's one quiet ask, at the end of a feature the visitor has tried (lib/demoAsk.ts). Never a wall. */
import { useNavigate } from "react-router-dom";
import { closeDemoGate, dismissDemoAsk, demoSignupUrl, DEMO_ASK_ACTION, DEMO_ASK_SKIP, DEMO_START_ACTION, DEMO_WORDS, isDemoSample, useDemoAskOpen, useDemoGate, type DemoFeature } from "../lib/demoAsk.ts";
import { useSession } from "../session.tsx";
import { AnchorButton, Button, Notice, Sheet } from "./ui.tsx";

export function DemoAsk({ feature }: { feature: DemoFeature }) {
  const s = useSession();
  const open = useDemoAskOpen(feature);
  if (!open || !isDemoSample(s.org)) return null;
  const w = DEMO_WORDS[feature];
  return (
    <div data-testid={`demo-ask-${feature}`}>
      <Notice tone="go" icon="spark" title={w.did}
        action={<><AnchorButton href={demoSignupUrl(feature)} icon="chevron">{DEMO_ASK_ACTION}</AnchorButton><Button variant="quiet" onClick={() => dismissDemoAsk(feature)}>{DEMO_ASK_SKIP}</Button></>}>
        <p>{w.gets}</p>
      </Notice>
    </div>
  );
}

/** The end of the sample: a clear next step, only when no feature ask is already on this screen. */
export function DemoEnd() {
  const s = useSession();
  const checks = useDemoAskOpen("checks");
  if (!isDemoSample(s.org) || checks) return null;
  return (
    <div data-testid="demo-end">
      <Notice tone="info" icon="spark" title="That's the sample café.">
        <p>Yours would have your kitchen's name, hours and team.</p>
        <span className="row"><AnchorButton href={demoSignupUrl("end")} icon="chevron">{DEMO_START_ACTION}</AnchorButton></span>
      </Notice>
    </div>
  );
}

/**
 * Past a feature's threshold, its next save opens this instead of doing it. "Keep looking around" goes Home, where every
 * feature not used up is still open; what they already did (records, the binder) stays viewable. Demo only.
 */
export function DemoGate() {
  const s = useSession();
  const feature = useDemoGate();
  const nav = useNavigate();
  if (!feature || !isDemoSample(s.org)) return null;
  const w = DEMO_WORDS[feature];
  const leave = () => { closeDemoGate(); nav("/"); };
  return (
    <Sheet title={w.did} onClose={closeDemoGate} closeLabel="Close">
      <div className="stack" data-testid="demo-gate">
        <p>{w.gets}</p>
        <div className="form-actions">
          <AnchorButton big href={demoSignupUrl(feature)} icon="chevron">{DEMO_ASK_ACTION}</AnchorButton>
          <Button variant="ghost" onClick={leave}>{DEMO_ASK_SKIP}</Button>
        </div>
      </div>
    </Sheet>
  );
}
